import {Feature} from "ol";
import {Point} from "ol/geom";
import {fromLonLat} from "ol/proj";
import {Fill, Icon, Style, Text} from "ol/style";
import {
    importancePointColorList,
    sentimentPointColorList
} from "@/components/MapPage/MainMap/constant";

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Отрисовка квадратной точки. (Для сантимента)
const drawRoundedRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
};

const getRectSize = (newsCount) => {
    if (newsCount < 100) return 22;
    return 36;
};

const createRoundedRectIcon = (color, newsCount = 1) => {
    const innerSize = getRectSize(newsCount);
    const outerBorder = newsCount <= 1 ? 0 : newsCount >= 100 ? 12 : Math.min(newsCount, 11);
    const whiteBorder = 2;
    const totalOffset = outerBorder + whiteBorder;

    const canvas = document.createElement("canvas");
    canvas.width = innerSize + totalOffset * 2;
    canvas.height = innerSize + totalOffset * 2;
    const ctx = canvas.getContext("2d");
    const r = 8;

    // 1. Внешняя полупрозрачная обводка
    if (outerBorder > 0) {
        drawRoundedRect(ctx, 0, 0, canvas.width, canvas.height, r + 4);
        ctx.fillStyle = hexToRgba(color, 0.7);
        ctx.fill();
    }

    // 2. Белая обводка
    drawRoundedRect(ctx, outerBorder, outerBorder, innerSize + whiteBorder * 2, innerSize + whiteBorder * 2, r + 1);
    ctx.fillStyle = "#FFF";
    ctx.fill();

    // 3. Основная заливка
    drawRoundedRect(ctx, totalOffset, totalOffset, innerSize, innerSize, r);
    ctx.fillStyle = color;
    ctx.fill();

    return canvas;
};

// Отрисовка круговой точки (Для важности).
const getCircleDiameter = (newsCount) => {
    if (newsCount <= 1) return 22;
    if (newsCount === 2) return 23;
    if (newsCount === 3) return 24;
    if (newsCount <= 11) return 25;
    return 32;
};

const createCircleIcon = (color, newsCount = 1) => {
    const diameter = getCircleDiameter(newsCount);
    const radius = diameter / 2;
    const outerBorder = newsCount <= 1 ? 0 : Math.min(newsCount, 11);
    const whiteBorder = 2;
    const totalOffset = outerBorder + whiteBorder;

    const canvas = document.createElement("canvas");
    canvas.width = diameter + totalOffset * 2;
    canvas.height = diameter + totalOffset * 2;
    const ctx = canvas.getContext("2d");

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // 1. Внешняя полупрозрачная обводка
    if (outerBorder > 0) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + totalOffset, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(color, 0.7);
        ctx.fill();
    }

    // 2. Белая обводка
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + whiteBorder, 0, Math.PI * 2);
    ctx.fillStyle = "#FFF";
    ctx.fill();

    // 3. Основная заливка
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    return canvas;
};

export const initializeNews = (map, newsList) => {
    const layers = map.getLayers();
    let newsVectorSource;

    // Поиск слоя с регионами
    layers.forEach((layer) => {
        if (layer.getSource().get("name") === "news") newsVectorSource = layer.getSource();
    });

    newsVectorSource.clear();

    if (newsList === null) return false;
    let index = 0;
    for (let news of newsList) {
        index += 1;
        const feature = new Feature({
            geometry: new Point(
                fromLonLat([news.city.longitude, news.city.latitude])),
            type: "news",
            newsList: news.cardsIds,
            cityName: news.city.city
        });
        const isSentiment = localStorage.getItem("avalMapActiveFilterType") === "sentiment";
        
        const color = isSentiment
            ? (sentimentPointColorList[news.rating.toString()])
            : (importancePointColorList[news.rating.toString()]);

        let imageStyle;
        if (isSentiment) {
            const canvas = createRoundedRectIcon(color, news.cardsIds.length);
            imageStyle = new Icon({
                img: canvas,
                imgSize: [canvas.width, canvas.height],
                anchor: [0.5, 0.5],
            });
        } else {
            const canvas = createCircleIcon(color, news.cardsIds.length);
            imageStyle = new Icon({
                img: canvas,
                imgSize: [canvas.width, canvas.height],
                anchor: [0.5, 0.5],
            });
        }

        // Получение числа количества новостей для отображения на точке.
        const getClusterLabel = (count) => {
            if (count <= 1) return "";
            if (count > 100) return "100+";
            return count.toString();
        };

        const newsPointStyle = new Style({
            image: imageStyle,
            text: new Text({
                scale: 1,
                offsetY: 2,
                font: "500 14px Roboto,sans-serif",
                overflow: true,
                text: getClusterLabel(news.cardsIds.length),
                fill: new Fill({
                    color: "#ffffff"
                }),
            }),
            zIndex: index,
        });

        feature.setStyle(newsPointStyle);

        newsVectorSource.addFeature(feature);

    }
};